import { Plus, Trash } from 'react-feather';
import CustomButton from '../../../components/custom-button/custom-btn.component';
import PageLayout from '../../../components/PageLayout';
import { useUrlState } from '../../../utils/useUrlState';
import { useState } from 'react';
import { ConfirmModal } from '../../../components/modal/confirm-modal/ConfirmModal.component';
import { Select, SelectItem, TextArea } from 'carbon-components-react';
import { capitalize, useReducerSpread } from '../../../utils/common.util';
import { useCustomMutation } from '../../../utils/useCustomMutation';
import { deleteMethod, postMethod } from '../../../api/common';
import useSWR from 'swr';
import { RemarkCategories } from './RemarkCategories';
import ComponentGuard from '../../../components/component-guard/ComponentGuard';

const initialData = {
  remark: '',
  academicReportRemarkCategoryId: '',
};

export default function AcademicRemarksPage() {
  const [query, setQuery] = useUrlState({
    category: remarksCategories[0].value,
  });

  const [isOpen, setIsOpen] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  const [formData, setFormData] = useReducerSpread(initialData);

  const { trigger, error, isMutating } = useCustomMutation(
    postLinks[query.category],
    (url, { arg }) => postMethod(url, arg),
  );

  const isTeacherRemark = query.category.includes('Teacher');

  const displayRemark = () => {
    let ending = '';
    if (isTeacherRemark) {
      ending = 'Remark';
    }
    return capitalize(query.category + ' ' + ending);
  };

  function handleSubmit() {
    const key = isTeacherRemark ? 'remark' : query.category;
    const payload = {
      remarkType: query.category,
      [key]: formData.remark,
      academicReportRemarkCategoryId: formData.academicReportRemarkCategoryId,
    };

    trigger(payload, {
      onSuccess: () => {
        setIsOpen('');
        setFormData(initialData);
        mutate();
      },
    });
  }

  const { data, isLoading, mutate } = useSWR(getLinks[query.category]);

  const {
    trigger: deleteTrigger,
    error: deleteError,
    isMutating: isDeleting,
  } = useCustomMutation(
    `${deleteLinks[query.category]}/${selectedItem?.id}`,
    (url, { arg }) => deleteMethod(url, arg),
  );

  const { data: remarkCategories } = useSWR(
    isOpen ? 'academic/grading/list-academic-report-remark-category' : null,
  );

  // const ARRC = 'academicReportRemarkCategory';

  return (
    <PageLayout
      breadcrumbs={[
        { label: 'Academics', to: '/academic' },
        { label: `Remarks Management`, active: true },
      ]}
      title={
        <div
          className="d-flex align-items-center flex-wrap"
          style={{ gap: '30px' }}
        >
          <h1 className="h-20">Remarks Management</h1>{' '}
          <ComponentGuard allowedRoles={['AcademicReportRemarksAccess']}>
            <button
              className="text-12 text-primary d-flex align-items-center font-bold pointer clean-btn "
              style={{ gap: '4px' }}
              onClick={() => setIsOpen('add')}
            >
              <Plus size="14" strokeWidth="2px" />
              <span>Add New {displayRemark()}</span>
            </button>
          </ComponentGuard>
          <button
            className="text-12 text-primary d-flex align-items-center font-bold pointer clean-btn "
            style={{ gap: '4px', marginLeft: 'auto' }}
            onClick={() => setIsOpen('categories')}
          >
            Remark Categories
          </button>
        </div>
      }
      isLoading={isLoading}
    >
      <div style={{ display: 'flex', gap: '30px' }}>
        {remarksCategories.map((category) => (
          <CustomButton
            key={category.value}
            classes={`pill text-12 font-bold ${query.category === category.value ? 'primary' : 'ash'}`}
            onClick={() => setQuery({ category: category.value })}
          >
            {category.label}
          </CustomButton>
        ))}
      </div>

      <div className="mt-30">
        {data?.length > 0 && (
          <table className="bx--data-table table-white">
            <thead>
              <tr>
                <th>ID</th>
                <th>Description</th>
                {isTeacherRemark && (
                  <>
                    <th>Category</th>
                    <th>Raw Score Range</th>
                  </>
                )}
                <ComponentGuard allowedRoles={['AcademicReportRemarksAccess']}>
                  <th>Action</th>{' '}
                </ComponentGuard>
              </tr>
            </thead>
            <tbody>
              {data?.map((item) => {
                return (
                  <tr key={item.id}>
                    <td>{item?.id}</td>
                    <td>{item?.remark || item?.conduct || item?.interest}</td>
                    {isTeacherRemark && (
                      <>
                        <td>{item?.academicReportRemarkCategory?.remark}</td>
                        <td>
                          {item?.academicReportRemarkCategory?.rawScoreRange}
                        </td>
                      </>
                    )}
                    <ComponentGuard
                      allowedRoles={['AcademicReportRemarksAccess']}
                    >
                      <td>
                        <button
                          className="text-12 text-primary d-flex align-items-center font-bold pointer clean-btn "
                          style={{ gap: '4px' }}
                          onClick={() => setSelectedItem(item)}
                        >
                          <Trash
                            size="14"
                            strokeWidth="2px"
                            className="text-danger"
                          />
                        </button>
                      </td>
                    </ComponentGuard>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {data?.length === 0 && (
          <p className="text-14 text-grey">No data available yet.</p>
        )}
      </div>

      <ConfirmModal
        title={`Add New ${displayRemark()}`}
        isOpen={isOpen === 'add'}
        closeModal={() => {
          setIsOpen('');
          setFormData(initialData);
        }}
        description={
          <>
            <div className="mt-20">
              <TextArea
                labelText={displayRemark()}
                placeholder={`Enter ${displayRemark()}`}
                rows={5}
                value={formData.remark}
                onChange={(e) =>
                  setFormData({
                    remark: e.target.value,
                  })
                }
              />
              {isTeacherRemark && (
                <Select
                  labelText="Remark Category"
                  className="mt-20"
                  value={formData.academicReportRemarkCategoryId}
                  onChange={(e) =>
                    setFormData({
                      academicReportRemarkCategoryId: e.target.value,
                    })
                  }
                >
                  <SelectItem selected value="" text="" />
                  {remarkCategories?.map((item) => (
                    <SelectItem
                      key={item.id}
                      text={item.remark}
                      value={item.id}
                    />
                  ))}
                </Select>
              )}
            </div>
          </>
        }
        actionText="ADD"
        onClick={handleSubmit}
        disabled={
          !formData.remark ||
          (isTeacherRemark && !formData.academicReportRemarkCategoryId)
        }
        isLoading={isMutating}
        errorMessage={error ? error.message : null}
        actionBtnClasses="primary"
      />

      <ConfirmModal
        title={`Delete ${displayRemark()}`}
        isOpen={selectedItem !== null}
        closeModal={() => setSelectedItem(null)}
        description={
          <>
            <div className="mt-20">
              Are you sure you want to delete this {displayRemark()}?
            </div>
            <div className="mt-20">
              "
              {selectedItem?.remark ||
                selectedItem?.conduct ||
                selectedItem?.interest}
              "
            </div>
          </>
        }
        actionText="DELETE"
        onClick={() => {
          deleteTrigger(null, {
            onSuccess: () => {
              setSelectedItem(null);
              mutate();
            },
          });
        }}
        isLoading={isDeleting}
        errorMessage={deleteError ? deleteError.message : null}
        actionBtnClasses="danger"
      />

      <RemarkCategories
        isOpen={isOpen === 'categories'}
        closeModal={() => setIsOpen('')}
        setIsOpen={setIsOpen}
      />
    </PageLayout>
  );
}

const remarksCategories = [
  { label: 'Teacher Remarks', value: 'Teacher' },
  { label: 'Headteacher Remarks', value: 'HeadTeacher' },
  { label: 'Conduct', value: 'conduct' },
  { label: 'Interests', value: 'interest' },
];

const postLinks = {
  Teacher: 'academic/grading/create-academic-report-remark',
  HeadTeacher: 'academic/grading/create-academic-report-remark',
  conduct: 'academic/grading/create-academic-report-conduct',
  interest: 'academic/grading/create-academic-report-interest',
};

const getLinks = {
  Teacher: 'academic/grading/list-academic-report-remark?remarkType=Teacher',
  HeadTeacher:
    'academic/grading/list-academic-report-remark?remarkType=HeadTeacher',
  conduct: 'academic/grading/list-academic-report-conduct',
  interest: 'academic/grading/list-academic-report-interest',
};

const deleteLinks = {
  Teacher: 'academic/grading/delete-academic-report-remark',
  HeadTeacher: 'academic/grading/delete-academic-report-remark',
  conduct: 'academic/grading/delete-academic-report-conduct',
  interest: 'academic/grading/delete-academic-report-interest',
};
